import React, { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

export default function RecordingControls({ clientId, parked }) {
  const [control, setControl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const sequence = useRef(0);
  const mounted = useRef(false);
  const saving = useRef(false);

  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    let timer;
    const refresh = async () => {
      if (saving.current) {
        timer = setTimeout(refresh, 5000);
        return;
      }
      const request = ++sequence.current;
      try {
        const result = await httpsCallable(functions, 'getClientRecordingControl')({ clientId });
        if (!cancelled && request === sequence.current && !saving.current) {
          setControl(result.data);
          setError('');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Unable to check recording status.');
          setControl((previous) => (previous ? { ...previous, online: false } : null));
        }
      } finally {
        if (!cancelled) timer = setTimeout(refresh, 5000);
      }
    };
    refresh();
    return () => { cancelled = true; mounted.current = false; clearTimeout(timer); sequence.current += 1; };
  }, [clientId, parked]);

  const change = async (action) => {
    if (saving.current) return;
    saving.current = true;
    const request = ++sequence.current;
    setBusy(true);
    setError('');
    try {
      const result = await httpsCallable(functions, 'setClientRecordingControl')({
        clientId, action, expectedRequestId: control.requestId,
      });
      if (mounted.current && request === sequence.current) setControl(result.data);
    } catch (err) {
      if (mounted.current && request === sequence.current) setError(err.message || 'Unable to change recording.');
    } finally {
      saving.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const status = control?.status || {};
  const pending = Boolean(control && control.requestId !== (status.requestId || ''));
  const recording = control?.online && status.recording === true;
  const ready = control?.online && status.enabled && status.runId && parked && status.state !== 'waiting_for_video';
  let statusText = 'Checking recording status…';
  if (control) {
    if (!control.online) statusText = pending
      ? 'Waiting for the camera to reconnect and apply your change.'
      : 'Recording status unavailable.';
    else if (pending) statusText = 'Waiting for the camera to apply your change…';
    else if (!status.enabled) statusText = 'Recording is unavailable on this camera.';
    else if (!parked) statusText = 'Animal sightings are recorded automatically.';
    else if (status.state === 'waiting_for_video') statusText = 'Waiting for camera video…';
    else if (status.state === 'events_only') statusText = 'Saving animal sightings. Force record saves the whole session.';
    else if (recording) statusText = 'Recording.';
    else statusText = 'Stopped — keeping up to 30 seconds ready.';
  }

  return (
    <div className="settingsQuickControls__card">
      <div className="settingsQuickControls__title">Recording</div>
      <p className="settingsInlineMeta">For manual and auto-follow.</p>
      <div className="settingsActionRow" role="group" aria-label="Recording controls">
        <button type="button" className="settingsButton settingsButton--ghost"
          disabled={busy || !ready} onClick={() => change('force_record')}>
          Force record
        </button>
        <button type="button" className="settingsButton settingsButton--ghost"
          disabled={!control || busy || !parked} onClick={() => change('force_stop')}>
          Force stop
        </button>
      </div>
      <p className="settingsInlineMeta" role="status" aria-live="polite">{statusText}</p>
      {error && <p className="settingsInlineMeta" role="alert">{error}</p>}
    </div>
  );
}
